<template>
  <div>
    <overlay :value="loading"></overlay>
    <v-card class="plan-maintainer pw-flush d-flex">
      <div class="plan-maintainer__form d-flex flex-column">
        <div class="plan-maintainer__header">
          <div class="text-h6 font-weight-bold">{{ plan === null ? 'Crear nuevo plan' : 'Editar plan' }}</div>
          <div class="text-body-2 grey--text text--darken-1">
            A la derecha ves cómo lo verá el cliente al contratar.
          </div>
        </div>

        <v-card-text class="plan-maintainer__body">
          <v-form ref="newPlanForm" v-model="formValid">
            <v-row dense>
              <v-col cols="6">
                <v-text-field
                  label="Nombre"
                  v-model="newPlan.name"
                  :rules="nameRules"
                  outlined
                  dense
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  label="Descripción interna"
                  v-model="newPlan.internalDescription"
                  outlined
                  dense
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-select
                  label="Tipo de plan"
                  v-model="newPlan.type"
                  :items="['Gratuito', 'De pago']"
                  outlined
                  dense
                  hide-details="auto"
                ></v-select>
              </v-col>
              <v-col cols="2">
                <v-text-field
                  label="Orden"
                  v-model="newPlan.order"
                  type="number"
                  outlined
                  dense
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="4">
                <div class="option-box">
                  <v-checkbox
                    v-model="newPlan.visible"
                    label="Visible en el onboarding"
                    class="mt-0 pt-0"
                    hide-details
                  ></v-checkbox>
                </div>
              </v-col>
            </v-row>

            <v-divider class="my-5"></v-divider>

            <v-row dense>
              <v-col cols="4">
                <v-select
                  label="País"
                  v-model="newPlan.country"
                  :items="countries.map(c => c.name)"
                  :loading="loadingCountries"
                  outlined
                  dense
                  hide-details="auto"
                  @change="onCountryChange"
                ></v-select>
              </v-col>
              <v-col v-if="showCurrencySelector" cols="3">
                <v-select
                  label="Moneda"
                  v-model="newPlan.currency"
                  :items="currencyOptions"
                  :disabled="currencyOptions.length <= 1"
                  outlined
                  dense
                  hide-details="auto"
                ></v-select>
              </v-col>
              <v-col>
                <v-text-field
                  label="Precio mensual (IVA incluido)"
                  v-model="monthlyPriceDisplay"
                  :rules="priceRules"
                  outlined
                  dense
                  hide-details="auto"
                ></v-text-field>
              </v-col>
            </v-row>

            <div class="text-body-2 font-weight-medium mt-4 mb-2">Intervalos que se ofrecen</div>
            <v-row dense>
              <v-col cols="6">
                <div class="option-box">
                  <v-checkbox
                    v-model="newPlan.letMonthlyPayment"
                    label="Pago mensual"
                    class="mt-0 pt-0"
                    hide-details
                  ></v-checkbox>
                </div>
              </v-col>
              <v-col cols="6">
                <div class="option-box">
                  <v-checkbox
                    v-model="newPlan.letAnnualPayment"
                    label="Pago anual (12 meses − 8%)"
                    class="mt-0 pt-0"
                    hide-details
                  ></v-checkbox>
                </div>
              </v-col>
            </v-row>
            <v-alert v-if="noIntervalSelected" type="warning" text dense class="mt-3 mb-0">
              Marca al menos un intervalo para poder guardar.
            </v-alert>

            <div class="d-flex align-start mt-6">
              <v-file-input
                label="Plantilla del contrato (PDF)"
                v-model="newPlan.contractTemplateFile"
                accept="application/pdf,.pdf"
                :rules="contractTemplateRules"
                :placeholder="currentTemplateName"
                :persistent-placeholder="!!newPlan.contractUrl"
                :truncate-length="200"
                prepend-icon=""
                prepend-inner-icon="mdi-file-pdf-box"
                outlined
                dense
                hide-details="auto"
                class="contract-template-input"
              >
                <template v-slot:selection="{ text }">
                  <span class="contract-template-name" :title="text">{{ text }}</span>
                </template>
              </v-file-input>
              <v-btn
                class="ml-3 view-document-btn"
                :disabled="!canViewDocument"
                color="primary"
                outlined
                rounded
                @click="openViewer"
              >
                Ver documento
              </v-btn>
            </div>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="justify-end px-6 py-4">
          <v-btn text color="primary" rounded @click="closeDialog">Cancelar</v-btn>
          <v-btn
            :disabled="!formValid || noIntervalSelected"
            color="primary"
            rounded
            depressed
            @click="validate"
          >
            Guardar
          </v-btn>
        </v-card-actions>
      </div>

      <aside class="plan-preview d-flex flex-column">
        <div class="plan-preview__eyebrow">Vista previa · cliente</div>
        <div>
          <div class="plan-preview__name">{{ newPlan.name || 'Plan sin nombre' }}</div>
          <div class="plan-preview__muted">
            {{ newPlan.visible ? 'Visible en el onboarding' : 'Oculto: no se ofrece a nuevos clientes' }}
          </div>
        </div>

        <div v-if="newPlan.letMonthlyPayment" class="plan-preview__card plan-preview__card--monthly">
          <div class="plan-preview__card-label">Mensual</div>
          <div>
            <span class="plan-preview__price">{{ formatMoney(monthlyAmount) }}</span>
            <span class="plan-preview__muted"> / mes</span>
          </div>
        </div>

        <div v-if="newPlan.letAnnualPayment" class="plan-preview__card plan-preview__card--annual">
          <div class="d-flex justify-space-between align-center">
            <span class="plan-preview__card-label">Anual</span>
            <span class="plan-preview__badge">Ahorra {{ formatMoney(annualDiscount) }}</span>
          </div>
          <div>
            <span class="plan-preview__price">{{ formatMoney(annualAmount) }}</span>
            <span class="plan-preview__annual-muted"> / año</span>
          </div>
          <div class="plan-preview__annual-muted">
            Equivale a {{ formatMoney(roundAmount(annualAmount / 12)) }} al mes
          </div>
        </div>

        <div v-if="noIntervalSelected" class="plan-preview__empty">
          El cliente no tendría ninguna forma de pagar este plan.
        </div>

        <v-spacer></v-spacer>

        <div class="plan-preview__formula">
          <div class="plan-preview__formula-title">Cómo se calcula el anual</div>
          <div class="d-flex justify-space-between">
            <span>{{ formatMoney(monthlyAmount) }} × 12</span>
            <span>{{ formatMoney(annualGross) }}</span>
          </div>
          <div class="d-flex justify-space-between">
            <span>Descuento 8%</span>
            <span>− {{ formatMoney(annualDiscount) }}</span>
          </div>
          <div class="d-flex justify-space-between plan-preview__formula-total">
            <span>Total</span>
            <span>{{ formatMoney(annualAmount) }}</span>
          </div>
        </div>
      </aside>
    </v-card>

    <pdf-viewer-dialog
      :value="viewerOpen"
      :src="viewerSrc"
      title="Plantilla del contrato"
      @input="onViewerInput"
    ></pdf-viewer-dialog>

    <!-- Clic afuera o Esc cierran sin guardar, igual que "Cancelar". -->
    <v-dialog v-model="conflictDialog" max-width="520">
      <v-card v-if="conflict" data-test="template-conflict-dialog">
        <v-card-title class="text-subtitle-1 font-weight-bold">
          Ya existe «{{ conflict.fileName }}»
        </v-card-title>
        <v-card-text>
          <!-- El backend solo pregunta cuando otros planes usan el archivo. -->
          <p class="mb-2">
            También lo usan: <strong>{{ conflict.usedByPlans.join(', ') }}</strong>. Si lo sobrescribes,
            cambia el contrato de todos esos planes.
          </p>
          <p class="mb-0 text-body-2">
            Al sobrescribir, el documento nuevo puede tardar unos minutos en verse por la caché del CDN.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text data-test="conflict-cancel" @click="resolveConflict(null)">Cancelar</v-btn>
          <v-btn outlined color="primary" data-test="conflict-rename" @click="resolveConflict('rename')">
            Guardar como copia
          </v-btn>
          <v-btn color="error" depressed data-test="conflict-overwrite" @click="resolveConflict('overwrite')">
            Sobrescribir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="error" :timeout="5000">
      {{ errorMessage }}

      <template v-slot:action="{ attrs }">
        <v-btn color="red" text v-bind="attrs" @click="error = false"> Entendido </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import Overlay from '../components/Overlay.vue';
import PlansService from '../services/plans.service';
import CountryService from '../services/countries.service';
import PdfViewerDialog from './PdfViewerDialog.vue';

// Misma regla que `plan-annual-price.utils.ts` en el backend: el anual no se
// define a mano, se deriva del mensual (12 meses con 8% de descuento). Aquí solo
// se usa para mostrarlo; el backend descarta cualquier `annualPrice` recibido.
const MONTHS_PER_YEAR = 12;
const ANNUAL_PAYMENT_FACTOR = 0.92;

// Mismo límite que valida el backend sobre el PDF decodificado.
const MAX_CONTRACT_TEMPLATE_BYTES = 10 * 1024 * 1024;
const GENERIC_SAVE_ERROR = 'Ocurrió un error guardando el plan, intenta más tarde';
const CONTRACT_TEMPLATE_ERRORS = {
  CONTRACT_TEMPLATE_INVALID_PDF: 'El archivo no es un PDF válido o está incompleto',
  CONTRACT_TEMPLATE_TOO_LARGE: 'El PDF supera 10 MB',
  CONTRACT_TEMPLATE_UPLOAD_FAILED: 'No se pudo subir el archivo, intenta nuevamente',
  CONTRACT_TEMPLATE_NAME_EXHAUSTED: 'Ya existen demasiados archivos con ese nombre, renómbralo'
};

const isPdfFile = file => file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '');

export default {
  components: { Overlay, PdfViewerDialog },
  name: 'PlanMaintainer',
  props: ['plan'],
  data() {
    return {
      loading: false,
      error: false,
      countries: [],
      loadingCountries: false,
      newPlan: {},
      formValid: false,
      nameRules: [v => !!v || 'El campo es requerido'],
      priceRules: [v => (v !== null && v !== undefined && v !== '') || 'El campo es requerido'],
      contractTemplateRules: [
        v => !v || isPdfFile(v) || 'El archivo debe ser un PDF',
        v => !v || v.size <= MAX_CONTRACT_TEMPLATE_BYTES || 'El PDF supera 10 MB'
      ],
      errorMessage: GENERIC_SAVE_ERROR,
      // Plantilla con el mismo nombre ya publicada: { fileName, usedByPlans }.
      conflict: null,
      conflictDialog: false,
      viewerOpen: false,
      viewerSrc: '',
      blobUrl: null,
      monthlyPriceText: ''
    };
  },
  computed: {
    canViewDocument() {
      return !!this.newPlan.contractTemplateFile || !!this.newPlan.contractUrl;
    },
    currentTemplateName() {
      const lastSegment = (this.newPlan.contractUrl || '').split('/').pop();
      try {
        return decodeURIComponent(lastSegment);
      } catch {
        // URL con escapes inválidos: se muestra tal cual en vez de romper el formulario.
        return lastSegment;
      }
    },
    selectedCountryObj() {
      return this.countries.find(c => c.name === this.newPlan.country);
    },
    currencyOptions() {
      if (this.selectedCountryObj?.iso === 'CL') return ['CLP', 'UF'];
      return this.selectedCountryObj ? [this.selectedCountryObj.currency] : [];
    },
    showCurrencySelector() {
      return !!this.selectedCountryObj;
    },
    monthlyPriceDisplay: {
      get() {
        return this.monthlyPriceText;
      },
      set(val) {
        this.monthlyPriceText = this.formatLiveInput(val);
        this.newPlan.monthlyPrice = this.parseNumberInput(this.monthlyPriceText);
      }
    },
    noIntervalSelected() {
      return !this.newPlan.letMonthlyPayment && !this.newPlan.letAnnualPayment;
    },
    monthlyAmount() {
      return Number(this.newPlan.monthlyPrice) || 0;
    },
    annualGross() {
      return this.roundAmount(this.monthlyAmount * MONTHS_PER_YEAR);
    },
    annualAmount() {
      return this.roundAmount(this.monthlyAmount * MONTHS_PER_YEAR * ANNUAL_PAYMENT_FACTOR);
    },
    annualDiscount() {
      return this.roundAmount(this.annualGross - this.annualAmount);
    }
  },
  async mounted() {
    this.newPlan = {};
    await this.loadCountries();
    this.parsePlan(this.plan !== null);
  },
  beforeDestroy() {
    this.revokeBlobUrl();
  },
  watch: {
    plan(val) {
      this.newPlan = {};
      this.$refs.newPlanForm.resetValidation();
      this.parsePlan(val !== null);
    },
    'newPlan.contractTemplateFile'() {
      this.revokeBlobUrl();
      // La decisión de sobrescribir o copiar era para el archivo anterior.
      delete this.newPlan.contractTemplateOnConflict;
    }
  },
  methods: {
    openViewer() {
      this.revokeBlobUrl();
      const file = this.newPlan.contractTemplateFile;
      if (file) this.blobUrl = URL.createObjectURL(file);
      this.viewerSrc = this.blobUrl || this.newPlan.contractUrl;
      this.viewerOpen = true;
    },
    onViewerInput(open) {
      this.viewerOpen = open;
      if (!open) this.revokeBlobUrl();
    },
    revokeBlobUrl() {
      if (!this.blobUrl) return;
      URL.revokeObjectURL(this.blobUrl);
      this.blobUrl = null;
    },
    showSaveError(message) {
      this.errorMessage = CONTRACT_TEMPLATE_ERRORS[message] || GENERIC_SAVE_ERROR;
      this.error = true;
    },
    // Un error al guardar: si la plantilla ya existe se pregunta qué hacer; si no, se avisa.
    handleSaveError(res) {
      if (res.message === 'CONTRACT_TEMPLATE_EXISTS' && res.details) {
        this.conflict = {
          fileName: res.details.fileName,
          usedByPlans: res.details.usedByPlans || []
        };
        this.conflictDialog = true;
        return;
      }
      this.showSaveError(res.message);
    },
    // `choice`: 'overwrite' | 'rename', o null para cancelar sin guardar.
    resolveConflict(choice) {
      this.conflictDialog = false;
      if (!choice) return;
      this.newPlan.contractTemplateOnConflict = choice;
      this.validate();
    },
    closeDialog() {
      this.$emit('close-dialog');
    },
    parsePlan(exist) {
      const typeMap = { b2b: 'Gratuito', b2c: 'De pago' };
      this.newPlan = {
        id: exist ? this.plan.id : null,
        name: exist ? this.plan.name : '',
        order: exist ? this.plan.order || 0 : 0,
        visible: exist ? this.plan.visible : true,
        internalDescription: exist ? this.plan.internalDescription : '',
        type: exist ? typeMap[this.plan.type] || this.plan.type || '' : '',
        country: exist ? this.plan.country : this.countries[0]?.name || '',
        currency: exist ? this.plan.currency || 'CLP' : this.countries[0]?.currency || 'CLP',
        lowerLimit: exist ? this.plan.lowerLimit ?? 6 : 6,
        upperLimit: exist ? this.plan.upperLimit ?? 120 : 120,
        imageURL: exist ? this.plan.imageURL : '',
        contractUrl: exist ? this.plan.contractUrl || null : null,
        contractTemplateFile: null,
        monthlyPrice: exist ? this.plan.monthlyPrice || 0 : 0,
        // Planes anteriores a estos flags no los traen; se asumen habilitados
        // para conservar el comportamiento previo (ambos intervalos cobrables).
        letMonthlyPayment: exist ? this.plan.letMonthlyPayment ?? true : true,
        letAnnualPayment: exist ? this.plan.letAnnualPayment ?? true : true
      };
      this.monthlyPriceText = this.formatNumberDisplay(this.newPlan.monthlyPrice);
      this.$nextTick(() => {
        if (this.$refs.newPlanForm) this.$refs.newPlanForm.validate();
      });
    },
    onCountryChange(countryName) {
      const country = this.countries.find(c => c.name === countryName);
      this.newPlan.currency = country ? country.currency : 'CLP';
    },
    // UF conserva 4 decimales; el resto de monedas se redondea a enteros.
    roundAmount(value) {
      if (this.newPlan.currency === 'UF') return Math.round(value * 10000) / 10000;
      return Math.round(value);
    },
    formatMoney(value) {
      const currency = this.newPlan.currency;
      if (currency === 'UF') {
        return `${value.toLocaleString('es-CL', { maximumFractionDigits: 4 })} UF`;
      }
      const amount = value.toLocaleString('es-CL', { maximumFractionDigits: 2 });
      if (currency === 'CLP' || !currency) return `$${amount}`;
      return `${currency} ${amount}`;
    },
    formatNumberDisplay(value) {
      if (value === null || value === undefined || value === '') return value;
      const num = Number(value);
      return isNaN(num) ? value : num.toLocaleString('es-CL', { maximumFractionDigits: 2 });
    },
    formatLiveInput(value) {
      if (value === null || value === undefined) return '';
      const digitsAndComma = String(value).replace(/[^\d,]/g, '');
      const [rawInt, ...rest] = digitsAndComma.split(',');
      const intPart = rawInt.replace(/^0+(?=\d)/, '');
      const withThousands = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      if (rest.length === 0) return withThousands;
      const decimals = rest.join('').slice(0, 2);
      return `${withThousands},${decimals}`;
    },
    parseNumberInput(value) {
      if (value === null || value === undefined || value === '') return value;
      const cleaned = String(value).replace(/\./g, '').replace(',', '.');
      const num = Number(cleaned);
      return isNaN(num) ? value : num;
    },
    async loadCountries() {
      this.loadingCountries = true;
      try {
        const res = await CountryService.getAllCountries(true);
        this.countries = res.status === 'ok' ? res.data || [] : [];
      } catch (error) {
        console.log(error);
        this.countries = [];
      } finally {
        this.loadingCountries = false;
      }
    },
    // Anteriormente era b2b and b2b2c, se reemplaza por Gratuito, ya que b2b pasa a ser 'Gratuito'.
    priceValidation() {
      if (this.newPlan.type === 'Gratuito') {
        return this.newPlan.monthlyPrice >= 0;
      }
      return true;
    },
    validate() {
      // Un plan sin ningún intervalo habilitado no se puede cobrar ni mostrar
      // en el onboarding, así que se bloquea antes de llegar al backend.
      if (this.noIntervalSelected) return;
      if (this.$refs.newPlanForm.validate() && this.priceValidation()) {
        if (this.plan === null) {
          this.createPlan();
        } else {
          this.updatePlan();
        }
      }
    },
    async createPlan() {
      const selectedCountry = this.countries.find(c => c.name === this.newPlan.country);
      // Se arma una copia: si el guardado falla (p. ej. plantilla repetida) el formulario
      // conserva sus valores visibles, como el tipo "Gratuito" en vez de "b2b".
      const payload = { ...this.newPlan };
      payload.countryId = selectedCountry ? selectedCountry.id : undefined;
      payload.flow = false;
      payload.description = '';
      if (!payload.currency) {
        payload.currency = selectedCountry ? selectedCountry.currency : 'CLP';
      }
      if (payload.type === 'Gratuito') payload.type = 'b2b';
      else if (payload.type === 'De pago') payload.type = 'b2c';
      payload.deletedBy = '';
      payload.imageURL = '';
      this.loading = true;
      try {
        const res = await PlansService.createPlan(payload, selectedCountry ? selectedCountry.iso : 'CL');
        if (res.status === 'ok') {
          this.$refs.newPlanForm.resetValidation();
          this.closeDialog();
        } else {
          this.handleSaveError(res);
        }
      } catch {
        this.showSaveError();
      } finally {
        this.loading = false;
      }
    },
    async updatePlan() {
      this.loading = true;
      try {
        const payload = { ...this.newPlan };
        const typeMapReverse = { Gratuito: 'b2b', 'De pago': 'b2c' };
        if (typeMapReverse[payload.type]) payload.type = typeMapReverse[payload.type];
        const res = await PlansService.updatePlan(payload.id, payload);
        if (res.status === 'ok') {
          this.$refs.newPlanForm.resetValidation();
          this.closeDialog();
        } else {
          this.handleSaveError(res);
        }
      } catch {
        this.showSaveError();
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
/* El nombre de la plantilla actual se muestra como placeholder: con el gris por defecto se leería deshabilitado. */
.contract-template-input ::v-deep input::placeholder {
  color: var(--pw-text);
  opacity: 1;
}
/* Vuetify recorta el nombre a 22 caracteres con un "…" al medio; se muestra completo y solo
   se corta al final cuando no cabe. El contenedor sigue siendo flex (así queda centrado). */
.contract-template-input ::v-deep .v-file-input__text {
  flex-wrap: nowrap;
  min-width: 0;
}
.contract-template-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.plan-maintainer {
  overflow: hidden;
  min-height: 50vh;
}
.plan-maintainer__form {
  flex: 1 1 auto;
  min-width: 0;
}
.plan-maintainer__header {
  padding: 24px 24px 16px;
  border-bottom: 1px solid #e6e9ef;
}
.plan-maintainer__body {
  flex-grow: 1;
  padding: 20px 24px;
}
.option-box {
  height: 40px;
  display: flex;
  align-items: center;
  padding: 0 12px;
  border: 1px solid rgba(0, 0, 0, 0.38);
  border-radius: 4px;
}
.plan-preview {
  /* Vuetify redondea la esquina inferior izquierda del último hijo de un v-card
     (`.v-card > *:last-child { border-bottom-left-radius: inherit }`); aquí esa
     esquina toca el formulario, así que debe ser recta. */
  border-bottom-left-radius: 0 !important;
  width: 340px;
  flex-shrink: 0;
  gap: 18px;
  padding: 24px;
  background: #13233f;
  color: #ffffff;
}
.plan-preview__eyebrow {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #a9b8d6;
}
.plan-preview__name {
  font-size: 22px;
  font-weight: 800;
  line-height: 1.25;
}
.plan-preview__muted {
  font-size: 14px;
  color: #c3cee3;
}
.plan-preview__card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 16px;
  border-radius: 12px;
}
.plan-preview__card--monthly {
  background: #1d3259;
  border: 1px solid #2f4a7a;
}
.plan-preview__card--annual {
  background: #ffffff;
  color: #13233f;
}
.plan-preview__card-label {
  font-size: 13px;
  font-weight: 700;
}
.plan-preview__price {
  font-size: 28px;
  font-weight: 800;
}
.plan-preview__annual-muted {
  font-size: 13px;
  color: #4a5670;
}
.plan-preview__badge {
  font-size: 12px;
  font-weight: 700;
  color: #145c39;
  background: #dcf2e5;
  padding: 3px 8px;
  border-radius: 999px;
}
.plan-preview__empty {
  padding: 16px;
  border: 1px dashed #4f6690;
  border-radius: 12px;
  font-size: 14px;
  color: #c3cee3;
}
.plan-preview__formula {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 14px;
  border-top: 1px solid #2f4a7a;
  font-size: 13px;
  color: #c3cee3;
}
.plan-preview__formula-title,
.plan-preview__formula-total {
  font-weight: 700;
  color: #ffffff;
}
</style>
